import React, { useRef, useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import PlayerDyn from '../components/PlayerDyn';
import Playlist from '../components/Playlist';
import Radio from '../components/Radio';
import AudioLab from '../components/AudioLab';
import MapPopup from '../components/map/MapPopup';
import Quests from '../components/quests/Quests';
import QrReader from '../components/QrReader';
import Password from '../components/Password';
import MediaSession from '../components/MediaSession';
import NavigationTabs from '../components/NavigationTabs';
import TrackDetails from '../components/TrackDetails';
import { fetchData } from '../components/utility/ApiRequests';
import { generateCustomStyleTag } from '../components/utility/ParseStyles';
import {getTrackMetadataValue} from '../components/utility/MetadataFetcher';
import { useContentActivator, parseJsonContent } from '../components/utility/ContentActivator';

import { WalkDataContext } from '../components/hooks/walkDataContext';
import { ConditionalDesktopStyles } from '../components/utility/ConditionalDesktopStyles';

import StoryDetails from '../components/StoryDetails';
import TonePlayer from '../components/audioControllers/tonePlayer/Tone';

const AudioPlayer = () => {
  const { walkSlug } = useParams();
  const {
    setStoryData,
    language,
    storyMetas,
    tracks,
    currentTrack, setCurrentTrack,
    playerSize,
    activeTab, setActiveTab,
    isPlaying, setIsPlaying,
    handleTabChange,
    password, setPassword,
    isPasswordValid,
    validatePassword,
    validTrackSlugs,
    passwordAttempted,
    storyFeatures,
    activeStyles
  } = useContext(WalkDataContext);

  const { activateContent } = useContentActivator();
  const [timeProgress, setTimeProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const progressBarRef = useRef();
  const [customStyles, setCustomStyles] = useState(null);
  const lastActivatedTrackRef = useRef(null); // Track which track was last activated

  // Combine story custom styles (includes global styles) + activated styles
  const combinedStyles = useMemo(() => {
    const storyStyle = customStyles || '';

    // Merge all activated styles into a single CSS object
    // Each activeStyle.css is in format: {body: "...", .card: "..."}
    // We need to combine them all
    if (activeStyles.length === 0) {
      return storyStyle;
    }

    // Collect all CSS strings (story + activated)
    const allCssStrings = [storyStyle, ...activeStyles.map(s => s.css)].filter(Boolean);

    // Join all CSS strings - parseCustomStyles will parse and merge all blocks
    return allCssStrings.join(' ');
  }, [customStyles, activeStyles]);

  // Memoize alle Callbacks für bessere Performance
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, setIsPlaying]);

  const onLoadedMetadata = useCallback(() => {
    const seconds = audioRef.current.duration;
    setDuration(seconds);
  }, []);

  const resetPlayer = useCallback(() => {
    setIsPlaying(false);
    setTimeProgress(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, [setIsPlaying]);

  // Throttled setTimeProgress für bessere Performance
  const throttledSetTimeProgress = useCallback((newProgress) => {
    setTimeProgress(newProgress);
  }, []);

  const handleTrack = useCallback((slug) => {
    const track = tracks.find(track => track.slug === slug);
    if (track) {
      setCurrentTrack(track);
      if (storyFeatures.includes("default-player")){
        if (track.metadata?.trackMeta_audiotrack?.[language] ) {
          audioRef.current.src = track.metadata.trackMeta_audiotrack[language];
          audioRef.current.load();
          audioRef.current.play().then(() => {
            setIsPlaying(true);
          }).catch(e => {
            console.error('Error playing the track:', e);
            setIsPlaying(false);
          });
        } else {
          console.error('Audio source is undefined');
        }
      } 
    }else {
        setActiveTab('info');   // bei falsch gescanntem qu code
        console.error('Track not found for slug:', slug);
      }
  }, [tracks, language, setCurrentTrack, setIsPlaying, storyFeatures, setActiveTab]);

  // Initial fetch effect
  useEffect(() => {
    const initFetch = async () => {
      try {
        const data = await fetchData('/api/getStory/' + walkSlug);
        setStoryData(data);

        // Load custom styling (includes global styles + manual custom styles)
        if (data.story.metadata?.storyMeta_customstyling?.[1]) {
          setCustomStyles(data.story.metadata.storyMeta_customstyling[1]);
        }
      } catch (error) {
        console.error('Fehler beim Laden des Walks: ', error);
      }
    };

    if (walkSlug) {
      initFetch();
    }
  }, [walkSlug, setStoryData]);

  // Content activation effect
  useEffect(()=>{
    if (isPlaying && currentTrack){
      // Use currentTrack slug + audio URL as key to allow re-activation when returning to a track
      const trackKey = `${currentTrack.slug}-${currentTrack.metadata?.trackMeta_audiotrack?.[language]}`;

      if (lastActivatedTrackRef.current === trackKey) {
        return;
      }

      lastActivatedTrackRef.current = trackKey;

      const trackActionString = getTrackMetadataValue(currentTrack, storyMetas, 1, 'trackMeta_activatescontent', null);
      if (trackActionString) {
        const trackAction = parseJsonContent(trackActionString ?? '[]');
        activateContent(trackAction);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentTrack, isPlaying, storyMetas, language]);

  // Optimiertes useMemo - timeProgress ABSICHTLICH WEGGELASSEN!
  const memoizedPlayerDyn = useMemo(() => (
    <PlayerDyn
      audioRef={audioRef}
      progressBarRef={progressBarRef}
      duration={duration}
      setTimeProgress={throttledSetTimeProgress}
      isPlaying={isPlaying}
      togglePlayPause={togglePlayPause}
      timeProgress={timeProgress}
      resetPlayer={resetPlayer}
      handleTrack={handleTrack}
    />
  ), [
    // timeProgress ABSICHTLICH WEGGELASSEN für bessere Performance!
    playerSize, 
    currentTrack, 
    storyMetas, 
    language, 
    duration, 
    togglePlayPause, 
    isPlaying, 
    resetPlayer, 
    handleTrack,
    throttledSetTimeProgress
  ]);

  // Memoize playlist and radio tracks
  const [playlistTracks, radioTracks] = useMemo(() => {
    if (!tracks) return [[], []];
  
    const playlist = [];
    const radio = [];
  
    tracks.forEach(track => {
      if (track.metadata?.trackMeta_radio) {
        radio.push(track);
      } else if (
        !storyMetas?.storyMeta_trackpasswords ||
        !track.metadata?.trackMeta_password ||
        validTrackSlugs.includes(track.slug)
      ) {
        playlist.push(track);
      }
    });
    return [playlist, radio];
  }, [tracks, validTrackSlugs, storyMetas]);

  return (
    <>
      <Helmet>
        <title>{storyMetas?.storyMeta_title?.[language] || 'Ortspieler'}</title>
        <meta name="description" content={storyMetas?.storyMeta_description?.[language] || 'Audio walking tour'} />
      </Helmet>
      <ConditionalDesktopStyles />
      {generateCustomStyleTag(combinedStyles)}
      <div className='mobile-wrapper'>
        <Container className='ml-125'>
          {!storyMetas || !tracks ? (
            <div>Loading...</div>
          ) : (
            <>
              <MediaSession audioRef={audioRef} />
              
              {storyFeatures.includes("tone-player") &&
                <TonePlayer/>
              }

              {storyFeatures.includes("default-player") &&
                <audio 
                  ref={audioRef} 
                  crossOrigin="anonymous" 
                  onLoadedMetadata={onLoadedMetadata} 
                  onEnded={resetPlayer}
                >
                  {currentTrack?.metadata?.trackMeta_audiotrack?.[language] && (
                    <source src={currentTrack.metadata.trackMeta_audiotrack[language]} type="audio/mpeg" />
                  )}
                  Your browser does not support the audio element.
                </audio>
              }

              {storyFeatures.includes("default-player") && currentTrack && memoizedPlayerDyn}

              {playlistTracks && (
                <>
                  {storyFeatures.includes("info") && activeTab === 'info' && (
                    <section aria-label="Story information">
                      <StoryDetails />
                      <div className='miniplayer-spacer'></div>
                      <div className='navmenu-spacer'></div>
                    </section>
                  )}

                  {storyFeatures.includes("playlist") && activeTab === 'playlist' && (
                    <section aria-label="Playlist">
                      <Playlist
                        handleTrack={handleTrack}
                      />
                      <div className='miniplayer-spacer'></div>
                      <div className='navmenu-spacer'></div>
                    </section>
                  )}

                  {storyFeatures.includes("lab") && activeTab === 'lab' && (
                    <section aria-label="Audio lab">
                      <AudioLab
                        handleTrack={handleTrack}
                      />
                    </section>
                  )}

                  {(storyFeatures.includes("track-details") || storyFeatures.includes("track-details-no-icon")) && activeTab === 'track-details' && (
                    <section aria-label="Track details">
                      <TrackDetails />
                    </section>
                  )}

                  {storyFeatures.includes("radio") && activeTab === 'radio' && (
                    <section aria-label="Radio">
                      <Radio
                        tracks={radioTracks}
                        handleTrack={handleTrack}
                        language={language}
                        metas={storyMetas}
                      />
                      <div className='miniplayer-spacer'></div>
                      <div className='navmenu-spacer'></div>
                    </section>
                  )}

                  {storyFeatures.includes("password") && activeTab === 'password' && (
                    <section aria-label="Password entry">
                      <Password
                        password={password}
                        setPassword={setPassword}
                        validatePassword={validatePassword}
                        isPasswordValid={isPasswordValid}
                        passwordAttempted={passwordAttempted}
                      />
                      <div className='miniplayer-spacer'></div>
                      <div className='navmenu-spacer'></div>
                    </section>
                  )}

                  {storyFeatures.includes("quest") && activeTab === 'quest' && (
                    <section aria-label="Quests">
                      <Quests
                        language={language}
                      />
                    </section>
                  )}

                  {storyFeatures.includes("map") && activeTab === 'map' && (
                    <section aria-label="Map">
                      <MapPopup
                        handleTrack={handleTrack}
                        currentTrackSlug={currentTrack ? currentTrack.slug : 0}
                        paths={null}
                      />
                    </section>
                  )}

                  {storyFeatures.includes("qr") && activeTab === 'qr' && (
                    <section aria-label="QR code scanner">
                      <QrReader
                        tracks={tracks}
                        walkSlug={walkSlug}
                        handleTrack={handleTrack}
                        setActiveTab={setActiveTab}
                      />
                    </section>
                  )}
                </>
              )}

              <NavigationTabs activeTab={activeTab} onTabChange={handleTabChange} />
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default AudioPlayer;